/**
 * Browser-side XML utilities used by the Parse page editor:
 *  - {@link formatXml} pretty-prints a Cognos report so collapsed,
 *    minified payloads (most real exports!) are readable.
 *  - {@link validateXml} runs the input through {@code DOMParser},
 *    surfaces parser errors, and returns a small structural summary so
 *    the editor can show "Valid · 5,356 bytes · 1 root · 412 elements"
 *    instead of a one-bit ok/not-ok badge.
 *
 * <p>No external deps — both functions are ~30-50 lines on top of the
 * platform's built-in XML primitives.
 */

export type XmlValidation =
  | {
      ok: true;
      bytes: number;
      rootName: string;
      elementCount: number;
      namespace?: string;
    }
  | {
      ok: false;
      message: string;
      line?: number;
      column?: number;
    };

/**
 * Validate XML using {@code DOMParser}. Browsers signal failure by
 * inserting a {@code <parsererror>} element into the result document
 * (different shapes in Chromium vs Firefox vs Safari, but they all
 * include a human-readable message). We dig that out, plus best-effort
 * line/column extraction from the message text.
 */
export function validateXml(input: string): XmlValidation {
  const trimmed = (input ?? "").trim();
  if (!trimmed) {
    return { ok: false, message: "XML is empty" };
  }
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(trimmed, "application/xml");
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) };
  }

  const errEl =
    doc.getElementsByTagName("parsererror")[0] ??
    doc.getElementsByTagNameNS("*", "parsererror")[0];
  if (errEl) {
    const text = (errEl.textContent ?? "Invalid XML").trim().split("\n")[0] ?? "Invalid XML";
    const lineMatch = /line[^0-9]*([0-9]+)/i.exec(text);
    const colMatch = /column[^0-9]*([0-9]+)/i.exec(text);
    return {
      ok: false,
      message: text,
      line: lineMatch ? Number(lineMatch[1]) : undefined,
      column: colMatch ? Number(colMatch[1]) : undefined,
    };
  }

  const root = doc.documentElement;
  if (!root) return { ok: false, message: "no root element" };

  return {
    ok: true,
    bytes: trimmed.length,
    rootName: root.localName ?? root.nodeName,
    elementCount: doc.getElementsByTagName("*").length,
    namespace: root.namespaceURI ?? undefined,
  };
}

/**
 * Pretty-print XML by tokenising the input and re-emitting with
 * indentation. Tolerant of:
 *  - the XML declaration ({@code <?xml … ?>});
 *  - comments and CDATA blocks (preserved verbatim, never folded);
 *  - self-closing tags;
 *  - mixed content (text adjacent to children stays on its own line).
 *
 * <p>Falls back to the original input if a parse anomaly would produce
 * worse output than what the user pasted — never lossy.
 */
export function formatXml(input: string, indent = "  "): string {
  const src = (input ?? "").trim();
  if (!src) return "";

  // Fast token regex: declaration, comment, CDATA, or any tag/text run.
  const tokenRe = /<\?[\s\S]*?\?>|<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?]]>|<[^>]+>|[^<]+/g;
  const tokens = src.match(tokenRe);
  if (!tokens) return src;

  let depth = 0;
  const out: string[] = [];
  let lastWasOpen = false; // tracks whether the previous token was an open tag (controls inline text)

  for (const tok of tokens) {
    if (tok.startsWith("<?")) {
      out.push(tok);
      lastWasOpen = false;
      continue;
    }
    if (tok.startsWith("<!--") || tok.startsWith("<![CDATA[")) {
      out.push(indent.repeat(depth) + tok);
      lastWasOpen = false;
      continue;
    }
    if (tok.startsWith("<")) {
      const isClose = tok.startsWith("</");
      const isSelfClose = tok.endsWith("/>");
      if (isClose) depth = Math.max(0, depth - 1);
      out.push(indent.repeat(depth) + tok);
      if (!isClose && !isSelfClose) {
        depth += 1;
        lastWasOpen = true;
      } else {
        lastWasOpen = false;
      }
      continue;
    }

    // Text run between tags. Drop whitespace-only runs (they're just
    // the original indentation); keep meaningful text on its own line.
    const text = tok.replace(/\s+/g, " ").trim();
    if (!text) continue;
    out.push(indent.repeat(depth) + text);
    lastWasOpen = false;
  }

  // Rebuild with newlines, but keep the XML declaration on the same line
  // it began on so editors that highlight the prolog handle it correctly.
  return out.join("\n").replace(/(<\?[\s\S]*?\?>)\n/, "$1\n");
}

/**
 * Strip whitespace-only text nodes and re-flatten — useful for "round
 * trip my pretty-printed XML back into the wire form before sending."
 */
export function minifyXml(input: string): string {
  const src = (input ?? "").trim();
  if (!src) return "";
  return src.replace(/>\s+</g, "><").replace(/\s+/g, " ").trim();
}
